import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth, can } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import api from "@/lib/api";
import {
  LayoutDashboard, Users, Baby, FileText, CalendarDays, Receipt,
  Wallet, Settings, ScrollText, LogOut, Building2, UserCog, ChevronDown,
  ClipboardList, MessageSquare, Webhook, Smartphone, Menu, BarChart3, Bell, Sparkles,
  TrendingUp,
  CalendarClock, ExternalLink, BadgeEuro, AlertTriangle, Pill, Activity, Shield, Clock, Package
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import SupportModeBanner from "@/components/SupportModeBanner";
import KdvSwitcher from "@/components/KdvSwitcher";

const ROLE_LABEL_MAP = {
  platform_admin: "Platform-superadmin",
  directeur: "Directeur",
  locatiemanager: "Locatiemanager",
  administratie: "Administratie",
  financieel: "Financieel medewerker",
  pedagogisch: "Pedagogisch medewerker",
  pedagogisch_medewerker: "Medewerker (persoonlijk)",
  groep_account: "Groepsaccount",
  beheerder: "Beheerder",
  ouder: "Ouder",
};
/** Onderscheid platform-super (geen organization_id) vs. tenant-directeur
 * (superadmin binnen 1 organisatie). Tijdens supportmodus toont het label
 * "Platform-superadmin · supportmodus" zodat er nooit verwarring is over
 * wie de gebruiker echt is. */
const roleLabel = (u) => {
  if (!u) return "";
  if (u._impersonating) return "Platform-superadmin · supportmodus";
  if (u.role === "superadmin") {
    return u.organization_id ? "Directeur / Organisatiebeheerder" : "Platform-superadmin";
  }
  return ROLE_LABEL_MAP[u.role] || u.role;
};

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, module: "dashboard", end: true,
    hideRoles: ["pedagogisch", "pedagogisch_medewerker", "groep_account"] },
  // Groep-dashboard: uitsluitend voor groepslogin
  { to: "/app/groep-dashboard", label: "Groep-dashboard", icon: Users, module: "planning",
    onlyRoles: ["groep_account"] },
  // Persoonlijk dashboard: uitsluitend voor persoonlijke medewerker
  { to: "/app/medewerker-dashboard", label: "Mijn dashboard", icon: UserCog, module: "dashboard",
    onlyRoles: ["pedagogisch_medewerker"] },
  { to: "/app/directie", label: "Directie-cockpit", icon: TrendingUp, module: "dashboard",
    showRoles: ["superadmin", "platform_admin", "locatiemanager", "directeur", "financieel"] },
  { to: "/app/organisatie-instellingen", label: "Instellingen", icon: Settings, module: "dashboard",
    showRoles: ["superadmin", "platform_admin", "directeur", "beheerder"] },
  {
    label: "Beheer", section: true, hideRoles: ["pedagogisch", "pedagogisch_medewerker", "groep_account"], items: [
      { to: "/app/kinderen", label: "Kinderen", icon: Baby, module: "children" },
      { to: "/app/ouders", label: "Ouders", icon: Users, module: "parents" },
      { to: "/app/contracten", label: "Contracten", icon: FileText, module: "contracts" },
      { to: "/app/medewerkers", label: "Medewerkers", icon: UserCog, module: "staff" },
      { to: "/app/groepsaccounts", label: "Groepsaccounts", icon: Users, module: "users",
        showRoles: ["superadmin", "platform_admin", "directeur", "beheerder", "locatiemanager"] },
      { to: "/app/wachtlijst", label: "Wachtlijst", icon: ClipboardList, module: "wachtlijst", showRoles: ["superadmin", "platform_admin", "directeur", "administratie"] },
      { to: "/app/aanmeldingen", label: "Aanmeldingen", icon: ClipboardList, module: "aanmeldingen", showRoles: ["superadmin", "platform_admin"] },
    ]
  },
  { to: "/app/planning", label: "Presentielijst", icon: CalendarDays, module: "planning",
    hideRoles: ["pedagogisch_medewerker"] },
  { to: "/app/mijn-rooster", label: "Mijn rooster", icon: CalendarClock, module: "planning",
    onlyRoles: ["pedagogisch", "pedagogisch_medewerker"] },
  { to: "/app/rooster", label: "Roostering", icon: CalendarClock, module: "staff",
    hideRoles: ["pedagogisch", "pedagogisch_medewerker", "financieel", "groep_account"] },
  { to: "/groep", label: "Groepsapp", icon: Smartphone, module: "planning", external: true,
    hideRoles: ["pedagogisch", "pedagogisch_medewerker", "groep_account"] },
  { to: "/app/communicatie", label: "Schriftje & communicatie", icon: MessageSquare, module: "schriftje",
    hideRoles: ["pedagogisch", "pedagogisch_medewerker"] },
  { to: "/app/activiteiten", label: "Activiteiten", icon: Sparkles, module: "activiteiten",
    hideRoles: ["pedagogisch_medewerker"] },
  {
    label: "Financieel", section: true, hideRoles: ["pedagogisch", "pedagogisch_medewerker", "locatiemanager", "groep_account"], items: [
      { to: "/app/facturering", label: "Facturering", icon: Receipt, module: "invoices" },
      { to: "/app/debiteuren", label: "Debiteuren", icon: Wallet, module: "invoices" },
      { to: "/app/salaris", label: "Salaris", icon: BadgeEuro, module: "salary" },
    ]
  },
  { to: "/app/webhooks", label: "Koppelingen", icon: Webhook, module: "webhooks",
    hideRoles: ["pedagogisch_medewerker", "groep_account"] },
  { to: "/app/rapportages", label: "Rapportages", icon: BarChart3, module: "reports", showRoles: ["superadmin", "platform_admin"] },
  { to: "/app/audit", label: "Logboek", icon: ScrollText, module: "audit",
    hideRoles: ["pedagogisch_medewerker", "groep_account"] },
  { to: "/app/instellingen", label: "Instellingen", icon: Settings, module: "locations", showRoles: ["superadmin", "platform_admin", "directeur"] },
  { to: "/app/bkr-instellingen", label: "BKR-afwijktijden", icon: Settings, module: "locations", showRoles: ["superadmin", "platform_admin", "directeur", "beheerder", "locatiemanager"] },
  { to: "/app/incidenten", label: "Incidenten", icon: AlertTriangle, module: "incidenten",
    hideRoles: ["financieel", "pedagogisch_medewerker"] },
  { to: "/app/medicatie", label: "Medicatie", icon: Pill, module: "medicatie",
    hideRoles: ["financieel", "ouder", "pedagogisch_medewerker"] },
  { to: "/app/bkr-timeline", label: "BKR-timeline", icon: Activity, module: "planning", showRoles: ["superadmin", "platform_admin", "directeur", "locatiemanager"] },
  { to: "/app/beveiliging", label: "Beveiliging", icon: Shield, module: "users", showRoles: ["superadmin", "platform_admin", "directeur", "locatiemanager"] },
  { to: "/app/inspectiepakket", label: "Inspectiepakket", icon: Package, module: "users", showRoles: ["superadmin", "platform_admin", "directeur", "locatiemanager"] },
  { to: "/app/inklok", label: "Inkloksysteem", icon: Clock, module: "planning",
    hideRoles: ["ouder", "platform_admin", "groep_account"] },
  // Onboarding-management/Platform-beheer/Uitnodigingen zijn platform-super
  // features — verplaatst naar /platform. Alleen de onboarding-wizard (voor
  // het intake-formulier) blijft binnen de KDV-app zichtbaar.
  { to: "/app/onboarding-wizard", label: "Onboarding wizard", icon: Settings, showRoles: ["superadmin", "beheerder"] },
  { to: "/app/gebruikers", label: "Gebruikers", icon: Settings, showRoles: ["superadmin", "beheerder"] },
  { to: "/app/migratie", label: "Migratie", icon: Settings, showRoles: ["superadmin"] },
];

function NavItem({ to, label, icon: Icon, end, onNavigate }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ` +
        (isActive
          ? "bg-[color:var(--sage-100)] text-[color:var(--sage)]"
          : "text-neutral-600 hover:text-[color:var(--sage)] hover:bg-neutral-100")
      }
    >
      <Icon className="h-4 w-4" strokeWidth={2} />
      <span>{label}</span>
    </NavLink>
  );
}

function SidebarContent({ user, onNavigate }) {
  const [tenant, setTenant] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        // /tenant/me is scoped voor de ingelogde gebruiker en werkt voor alle
        // rollen (superadmin, locatiemanager, ouder). Voorheen: /organizations
        // — dat geeft 403 voor niet-superadmins.
        const r = await api.get("/tenant/me");
        const t = r.data;
        setTenant(t);
        // Zet PWA manifest + apple-touch-icon naar tenant-specifieke variant
        // zodat het geïnstalleerde app-icoon het opvang-logo toont.
        if (t?.id) {
          const backend = process.env.REACT_APP_BACKEND_URL || "";
          const manifestHref = `${backend}/api/manifest.json?org_id=${encodeURIComponent(t.id)}`;
          let link = document.querySelector('link[rel="manifest"]');
          if (link) link.setAttribute("href", manifestHref);
          if (t.logo_url) {
            const iconUrl = t.logo_url.startsWith("http") ? t.logo_url : `${backend}${t.logo_url}`;
            let apple = document.querySelector('link[rel="apple-touch-icon"]');
            if (!apple) {
              apple = document.createElement("link");
              apple.setAttribute("rel", "apple-touch-icon");
              document.head.appendChild(apple);
            }
            apple.setAttribute("href", iconUrl);
          }
        }
      } catch { /* ignore */ }
    })();
  }, []);
  const tenantName = tenant?.name;
  const tenantLogo = tenant?.logo_url;
  return (
    <div className="h-full flex flex-col">
      <div className="h-20 flex items-center gap-3 px-5 border-b border-neutral-200 shrink-0">
        {tenantLogo ? (
          <img src={tenantLogo.startsWith("http") ? tenantLogo : `${process.env.REACT_APP_BACKEND_URL}${tenantLogo}`}
               alt="logo" className="w-11 h-11 rounded-lg object-cover shrink-0" data-testid="sidebar-logo" />
        ) : (
          <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0" style={{ background: "var(--sage)" }}>
            <Baby className="h-6 w-6 text-white" strokeWidth={2.4} />
          </div>
        )}
        <div className="min-w-0">
          <div className="font-display text-[15px] leading-tight text-neutral-900 truncate" data-testid="sidebar-tenant-name">
            {tenantName || <span className="inline-block w-32 h-4 bg-neutral-100 rounded animate-pulse" aria-hidden />}
          </div>
          <div className="text-[9px] tracking-wide text-neutral-500 leading-tight mt-0.5">
            Powered by <span className="font-semibold" style={{ color: "var(--sage)" }}>Webster</span>
          </div>
          <div className="text-[9px] italic text-neutral-400 leading-tight">Kinderopvang slim geregeld</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {NAV.map((item, idx) => {
          if (item.hideRoles?.includes(user?.role)) return null;
          if (item.showRoles && !item.showRoles.includes(user?.role)) return null;
          if (item.section) {
            const visible = item.items.filter(it =>
              can(user, it.module, "read") &&
              !it.hideRoles?.includes(user?.role) &&
              (!it.showRoles || it.showRoles.includes(user?.role))
            );
            if (visible.length === 0) return null;
            return (
              <div key={idx} className="pt-4 pb-1">
                <div className="px-3 pb-1 text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">
                  {item.label}
                </div>
                {visible.map(it => <NavItem key={it.to} {...it} onNavigate={onNavigate} />)}
              </div>
            );
          }
          if (item.onlyRoles && !item.onlyRoles.includes(user?.role)) return null;
          if (item.hideRoles?.includes(user?.role)) return null;
          if (!can(user, item.module, "read")) return null;
          return <NavItem key={item.to} {...item} onNavigate={onNavigate} />;
        })}
      </nav>
      <div className="p-3 border-t border-neutral-200 text-[11px] text-neutral-500 shrink-0">
        <div>Wet Kinderopvang · AVG · LRK</div>
        <div className="text-neutral-400 mt-1">
          Powered by <span className="font-semibold text-[color:var(--sage)]">Webster</span> · v1.0
        </div>
      </div>
    </div>
  );
}

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { items: notifs, unread, markRead } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeTenant, setActiveTenant] = useState(null);
  const initials = user?.name?.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase() || "U";

  // Haal ECHTE tenant-info op zodat we nooit hardcoded "Kinderopvang Amsterdam"
  // tonen aan een platform-super in supportmodus.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/tenant/me");
        setActiveTenant(r.data || null);
      } catch { setActiveTenant(null); }
    })();
  }, [location.pathname]);

  const isDemoTenant = activeTenant?.id === "demo_regenboog";
  const orgLabel = activeTenant?.name || "—";

  // Auto-close mobile drawer on route change
  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="min-h-screen flex flex-col bg-[color:var(--bone)] subtle-grain">
      <SupportModeBanner />
      <div className="flex flex-1 min-h-0">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-neutral-200 bg-white flex-col" data-testid="sidebar">
        <SidebarContent user={user} />
      </aside>

      {/* Mobile sidebar (Sheet drawer) */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-72 bg-white" data-testid="sidebar-mobile">
          <SidebarContent user={user} onNavigate={closeMobile} />
        </SheetContent>
      </Sheet>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 sm:h-16 border-b border-neutral-200 bg-white/80 backdrop-blur-sm flex items-center justify-between px-3 sm:px-4 lg:px-6 gap-2" data-testid="topbar">
          <div className="flex items-center gap-2 min-w-0">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden shrink-0" data-testid="mobile-menu-trigger" aria-label="Menu openen">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
            </Sheet>
            {/* Mobile brand (visible when sidebar hidden) */}
            <div className="lg:hidden flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0" style={{ background: "var(--sage)" }}>
                <Baby className="h-4 w-4 text-white" />
              </div>
              <div className="font-display text-sm leading-tight truncate">Webster<span className="text-[color:var(--terracotta)]">.</span></div>
            </div>
            {/* Desktop org label + KDV-switcher */}
            <div className="hidden lg:flex items-center gap-3 min-w-0">
              <KdvSwitcher activeTenant={activeTenant} onSwitched={() => {}} />
              {isDemoTenant && (
                <span data-testid="demo-badge"
                      className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 font-semibold">
                  Demo-omgeving
                </span>
              )}
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="notifications-trigger"
                onClick={() => { if (unread > 0) markRead(); }}
                className="relative rounded-full p-2 hover:bg-neutral-100 transition-colors shrink-0"
                aria-label="Notificaties">
                <Bell className="h-5 w-5 text-neutral-600" />
                {unread > 0 && (
                  <span data-testid="notifications-badge"
                    className="absolute top-1 right-1 min-w-[16px] h-4 rounded-full bg-[color:var(--terracotta)] text-white text-[10px] flex items-center justify-center px-1 font-semibold">
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 bg-white max-h-96 overflow-y-auto" data-testid="notifications-panel">
              <DropdownMenuLabel className="flex items-center justify-between">
                <span>Notificaties</span>
                {notifs.length > 0 && (
                  <button
                    onClick={() => markRead()}
                    className="text-[11px] text-[color:var(--sage)] hover:underline"
                    data-testid="notifications-mark-all">
                    Alles als gelezen
                  </button>
                )}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {notifs.length === 0 && (
                <div className="p-4 text-xs text-neutral-500 text-center" data-testid="notifications-empty">
                  Nog geen notificaties.
                </div>
              )}
              {notifs.map(n => (
                <div key={n.id} data-testid={`notification-${n.id}`}
                  className={`px-3 py-2 border-l-2 ${n.read ? "border-transparent" : "border-[color:var(--terracotta)] bg-amber-50/30"}`}>
                  <div className="text-sm font-medium text-neutral-800 leading-tight">{n.title}</div>
                  {n.body && <div className="text-xs text-neutral-600 mt-0.5">{n.body}</div>}
                  <div className="text-[10px] text-neutral-400 mt-1">
                    {new Date(n.created_at).toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" className="flex items-center gap-2 sm:gap-3 rounded-full p-1 sm:pr-3 hover:bg-neutral-100 transition-colors shrink-0">
                <Avatar className="h-8 w-8 border border-neutral-200">
                  <AvatarFallback className="bg-[color:var(--sage)] text-white text-xs font-semibold">{initials}</AvatarFallback>
                </Avatar>
                <div className="hidden md:block text-left">
                  <div className="text-sm font-medium text-neutral-800 leading-tight">{user?.name}</div>
                  <div className="text-[11px] text-neutral-500 leading-tight">{roleLabel(user)}</div>
                </div>
                <ChevronDown className="hidden sm:block h-4 w-4 text-neutral-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-white">
              <DropdownMenuLabel>
                <div className="font-normal text-xs text-neutral-500">Aangemeld als</div>
                <div className="truncate">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem>
                <Badge className="bg-[color:var(--sage-100)] text-[color:var(--sage)] hover:bg-[color:var(--sage-100)]">
                  {roleLabel(user)}
                </Badge>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {/* Nanny Payroll externe app is vervangen door interne Salaris-tab */}
              <DropdownMenuItem
                data-testid="logout-btn"
                onClick={async () => { await logout(); navigate("/login"); }}
                className="text-red-600 focus:text-red-600"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Uitloggen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6 lg:p-8 pb-20 lg:pb-8" key={location.pathname}>
          {children}
        </main>
      </div>
      </div>
    </div>
  );
}
